import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { CentreService } from "src/app/_services/centre.service";
import {
  AddCentre,
  AddCentreError,
  AddCentreSuccess,
  CheckIsValidCentreName,
  CheckIsValidCentreNameError,
  CheckIsValidCentreNameSuccess,
  DeleteCentre,
  DeleteCentreError,
  DeleteCentreSuccess,
  GetAllCentre,
  GetAllCentreError,
  GetAllCentreSuccess,
  GetAllCentresByOrgIds,
  GetAllCentresByOrgIdsError,
  GetAllCentresByOrgIdsSuccess,
  GetAllCentresByOrgIdsTutorFlow,
  GetAllCentresByOrgIdsTutorFlowError,
  GetAllCentresByOrgIdsTutorFlowSuccess,
  GetCentreTable,
  GetCentreTableError,
  GetCentreTableLength,
  GetCentreTableLengthError,
  GetCentreTableLengthSuccess,
  GetCentreTableSuccess,
  GetCentresByOrgId,
  GetCentresByOrgIdError,
  GetCentresByOrgIdSuccess,
  GetCentresByUserIdAndOrgId,
  GetCentresByUserIdAndOrgIdError,
  GetCentresByUserIdAndOrgIdSuccess,
  ResetCentreTable,
  ResetCentreTableError,
  ResetCentreTableSuccess,
  SetCentreData,
  SetCentreDataSuccess,
  UpdateCentre,
  UpdateCentreError,
  UpdateCentreSuccess,
  UpdateCentreTable,
  UpdateCentreTableError,
  ValidateDeleteCentre,
  ValidateDeleteCentreError,
  ValidateDeleteCentreSuccess,
} from "./centre.actions";
import { catchError, map, switchMap } from "rxjs/operators";
import { of } from "rxjs";
import { Centre, CentreListDTO } from "src/app/_models/Centre";

@Injectable()
export class CentreEffects {
  constructor(
    private actions$: Actions,
    private centreService: CentreService
  ) {}

  public checkIsValidCentreNameEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckIsValidCentreName),
      switchMap((payload: { centreId: number, centreName: string; orgId: number }) =>
        this.centreService
          .isValidCentreName$({
            centreId: payload.centreId, 
            name: payload.centreName,
            organisationId: payload.orgId,
          })
          .pipe(
            map((result: boolean) => {
              return CheckIsValidCentreNameSuccess({ result: result });
            }),
            catchError((error: any) => {
              return of(CheckIsValidCentreNameError({ error: error }));
            })
          )
      )
    )
  );

  public addCentreEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddCentre),
      switchMap((payload) =>
        this.centreService.addCentre$(payload.addCentreDto).pipe(
          map((result: Centre) => {
            return AddCentreSuccess({ result: result });
          }),
          catchError((error: any) => {
            return of(AddCentreError({ error: error }));
          })
        )
      )
    )
  );

    public updateCentreTableEffect$ = createEffect(() => 
        this.actions$.pipe(
            ofType(UpdateCentreTable),
            switchMap((payload) =>
                this.centreService.updateCentreTableRecords$(payload.updateCentreTableDto).pipe(
                    map(() => {
                        return GetCentreTable({pageIndex: payload.updateCentreTableDto.pageIndex, isInitialLoad: false, isGetLength: payload.isGetlength});
                    }),
                    catchError((error: any) => {
                        return of(UpdateCentreTableError({error: error}));
                    })
                )
            )
        )
    );

    public getCentreTableEffect$ = createEffect(() => 
        this.actions$.pipe(
            ofType(GetCentreTable),
            switchMap((payload) =>
                this.centreService.getCentreTableRecord$(payload.pageIndex, payload.isInitialLoad).pipe(
                    map((result: CentreListDTO[]) => {
                        return GetCentreTableSuccess({result: result, isGetLength: payload.isGetLength});
                    }),
                    catchError((error: any) => {
                        return of(GetCentreTableError({error: error}));
                    })
                )
            )
        )
    );

  public getCentreTableLengthEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetCentreTableLength),
      switchMap(() =>
        this.centreService.getCentreTableLength$().pipe(
          map((result: number) => {
            return GetCentreTableLengthSuccess({ result: result });
          }),
          catchError((error: any) => {
            return of(GetCentreTableLengthError({ error: error }));
          })
        )
      )
    )
  );

  public getCentresByOrgId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetCentresByOrgId),
      switchMap((payload) =>
        this.centreService.getCentresByOrgId$(payload.orgId).pipe(
          map((result: Centre[]) => {
            return GetCentresByOrgIdSuccess({ result: result });
          }),
          catchError((error: any) => {
            return of(GetCentresByOrgIdError({ error: error }));
          })
        )
      )
    )
  );

  public getCentresByUserIdOrgId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetCentresByUserIdAndOrgId),
      switchMap((payload) =>
        this.centreService.getCentresByUserIdAndOrgId$(payload.userId, payload.orgId).pipe(
          map((result: Centre[]) => {
            return GetCentresByUserIdAndOrgIdSuccess({ result: result });
          }),
          catchError((error: any) => {
            return of(GetCentresByUserIdAndOrgIdError({ error: error }));
          })
        )
      )
    )
  );

  public resetCentreTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResetCentreTable),
      switchMap((payload) =>
        this.centreService.resetCentreTable$().pipe(
          map(() => {
            return ResetCentreTableSuccess({isInitialLoad: payload.isInitialLoad});
          }),
          catchError((error: any) => {
            return of(ResetCentreTableError({ error: error }));
          })
        )
      )
    )
  );

  public setCentreDataEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SetCentreData),
      map(() => {
        return SetCentreDataSuccess();
      })
    )
  );

  public updateCentreEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateCentre),
      switchMap((payload: {centre: Centre}) =>
        this.centreService.updateCentre$(payload.centre).pipe(
          map((centre: Centre) => {
            return UpdateCentreSuccess({ centre: centre });
          }),
          catchError((error: any) => {
            return of(UpdateCentreError({ error: error }));
          })
        )
      )
    )
  );

  public getAllCentre$ = createEffect(() => 
    this.actions$.pipe(
      ofType(GetAllCentre),
      switchMap(() => 
        this.centreService.getCentres$().pipe(
          map((centre: Centre[]) => {
            return GetAllCentreSuccess({ result: centre });
          }),
          catchError((error: any) => {
            return of(GetAllCentreError({ error: error }));
          })
        )
      )
    )
  );

  public deleteCentre$ = createEffect(() => 
    this.actions$.pipe(
      ofType(DeleteCentre),
      switchMap((payload) => 
        this.centreService.deleteCentre$(payload.id).pipe(
          map((result: boolean) => {
            return DeleteCentreSuccess({ isDeletedSuccessFully: result });
          }),
          catchError((error: any) => {
            return of(DeleteCentreError({ error: error }));
          })
        )
      )
    )
  );

  public validateDeleteCentre$ = createEffect(() => 
    this.actions$.pipe(
      ofType(ValidateDeleteCentre),
      switchMap((payload) => 
        this.centreService.validateDeleteCentre$(payload.id).pipe(
          map((result: boolean) => {
            return ValidateDeleteCentreSuccess({ isValid: result });
          }),
          catchError((error: any) => {
            return of(ValidateDeleteCentreError({ error: error }));
          })
        )
      )
    )
  );

  public getAllCentreByOrgIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetAllCentresByOrgIds),
      switchMap((payload) => 
        this.centreService.getAllCentresByOrgIds$(payload.orgIds).pipe(
          map((result: Centre[]) => {
            return GetAllCentresByOrgIdsSuccess({result: result});
          }),
          catchError((error: any) => {
            return of(GetAllCentresByOrgIdsError({error :error}));
          }),
        )
      )
    )
  );

  public GetAllCentresByUserIdAndOrgIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetAllCentresByOrgIdsTutorFlow),
      switchMap((payload) => 
        this.centreService.getCentresByOrgIdsTutorFlow$(payload.orgIds).pipe(
          map((result: Centre[]) => {
            return GetAllCentresByOrgIdsTutorFlowSuccess({result: result});
          }),
          catchError((error: any) => {
            return of(GetAllCentresByOrgIdsTutorFlowError({error :error}));
          }),
        )
      )
    )
  );
}
